import Head from "next/head";
import { connect } from "react-redux";

import FullWidthContainer from "@components/FullWidthContainer";
import ResponsiveShopBanner from "@components/ShopInShop/ResponsiveShopBanner";
import { getShopInShopCategoriesApi } from "@lib/api";
import { makeApiCall } from "@lib/api/helpers";
import AppleNavbar from "@components/ShopInShop/Apple/AppleNavbar";
import ExpandableProductList from "@components/ExpandableProductList";
import { trackProductClick } from "@store/tracker/actions";
import Heading from "@components/Heading";
import ErrorPage from "@containers/ErrorPage";
import VideoBanner from "@components/ShopInShop/VideoBanner";
import AppleFeaturedCategoriesList from "@components/ShopInShop/Apple/AppleFeaturedCategoriesList";
import ContentEditorView from "@components/ContentEditorView";

const AppleShop = ({
  categories,
  banners,
  menu,
  seo,
  footer,
  trackProductClick,
  weekly_deal_products,
}) => {
  if (!categories) {
    return <ErrorPage layout="404" />;
  }

  let parsedFooterObject = null;

  if (footer) {
    try {
      parsedFooterObject = JSON.parse(footer);
    } catch (error) {
      parsedFooterObject = false;
    }
  }

  const handleProductClick = (product, options) => {
    trackProductClick(product, options);
  };

  const hasTopBanner =
    banners &&
    banners.find(
      (banner) => banner.position === "top" && banner.type === "image"
    );

  const hasTopVideoBanner =
    banners &&
    banners.find(
      (banner) =>
        banner.position === "top" &&
        banner.type === "video" &&
        banner.video?.url
    );

  const bottomBanners =
    banners &&
    banners.filter(
      (banner) => banner.position === "bottom" && banner.type === "image"
    );

  return (
    <>
      <Head>
        {seo?.title && <title>{seo.title}</title>}
        {seo?.description && (
          <meta name="description" content={seo.description} />
        )}
      </Head>

      {menu && (
        <FullWidthContainer>
          <div className="bg-[#F5F5F7]">
            <nav className="flex items-center text-center gap-[50px] md:gap-[60px] py-[15px] md:py-[24px] container">
              <AppleNavbar menu={menu} />
            </nav>
          </div>
        </FullWidthContainer>
      )}

      {hasTopVideoBanner && (
        <FullWidthContainer>
          <div className="bg-black">
            <div className="md:container md:w-[80%] lg:w-[60%] mx-auto">
              <VideoBanner banner={hasTopVideoBanner} />
            </div>
          </div>
        </FullWidthContainer>
      )}

      {hasTopBanner && !hasTopVideoBanner && (
        <FullWidthContainer>
          <ResponsiveShopBanner banner={hasTopBanner} />
        </FullWidthContainer>
      )}

      <div className="mt-[40px] md:mt-[80px]">
        <AppleFeaturedCategoriesList
          categories={categories}
          onProductClick={handleProductClick}
        />
      </div>

      {weekly_deal_products && (
        <div className="mt-[20px] md:mt-[40px]">
          <Heading
            as="h2"
            title="Oferta saptamanii"
            classesProp="-tracking-[0.66px]"
          />

          <ExpandableProductList
            products={weekly_deal_products}
            desktopVisibleProducts={10}
            onProductClick={(product) => handleProductClick(product)}
          />
        </div>
      )}

      {parsedFooterObject && (
        <div className="mt-[40px] md:my-[50px] md:py-[50px] md:border-y md:border-[#EBEBEB]">
          <ContentEditorView content={parsedFooterObject} />
        </div>
      )}

      {bottomBanners?.length > 0 && (
        <div className="grid md:grid-cols-2 gap-[16px] md:gap-[24px]">
          {bottomBanners.map((banner, index) => (
            <ResponsiveShopBanner banner={banner} key={index} />
          ))}
        </div>
      )}
    </>
  );
};

AppleShop.pageLayout = "default";

AppleShop.getInitialProps = async () => {
  const response = await makeApiCall({
    apiArgs: [{ shopName: "apple" }],
    apiCall: getShopInShopCategoriesApi,
    returnResponse: true,
    formatResponse: (response) => response.data,
  });

  if (response && response.status) {
    const updatedReponse = { ...response };

    if (response.menu?.length === 0) {
      updatedReponse.menu = null;
    }

    if (response.categories?.length === 0) {
      updatedReponse.categories = null;
    }

    if (response.banners?.length === 0) {
      updatedReponse.banners = null;
    }

    if (response.footer?.length === 0) {
      updatedReponse.footer = null;
    }

    if (response.seo && Object.keys(response.seo).length === 0) {
      updatedReponse.seo = null;
    }

    if (response.weekly_deal_products?.length === 0) {
      updatedReponse.weekly_deal_products = null;
    }

    return updatedReponse;
  }

  return null;
};

export default connect(null, { trackProductClick })(AppleShop);
